import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/RestoreOutlined";
import { ViewContainer } from "@components/view-container";
import { OutlinedSelect } from "@components/outlined-select";
import { UserAutocomplete } from "@components/user-autocomplete";
import { UserIdentifierDisplay } from "@components/user-identifier-display";
import { useCapabilitiesAdmin, CapConfig, CapOverride } from "@hooks/use-capabilities-admin";
import { contentMaxWidth } from "@utils/colors";

// Roles shipped with the plugin — derived from is_protected flag returned by API.

// ── Role Matrix ──────────────────────────────────────────────────────────────

const RoleMatrix = ({
  config,
  saving,
  onToggle,
  onReset,
  onDelete,
}: {
  config: CapConfig;
  saving: boolean;
  onToggle: (role: string, cap: string, grant: boolean) => void;
  onReset: (role: string) => void;
  onDelete: (role: string) => void;
}) => {
  const roleEntries = Object.entries(config.roles ?? {}).sort(
    ([, a], [, b]) => {
      // Default/protected roles first, then custom roles alphabetically
      if (a.is_protected !== b.is_protected) return a.is_protected ? -1 : 1;
      return a.name.localeCompare(b.name);
    }
  );
  const sectionEntries = Object.entries(config.sections ?? {});

  const stickyCell: React.CSSProperties = {
    position: "sticky",
    left: 0,
    zIndex: 2,
  };

  return (
    <Paper elevation={2} sx={{ overflow: "hidden" }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6">Role Capabilities Matrix</Typography>
        <Typography variant="body2" color="text.secondary">
          Check a cell to grant a capability to that role. Changes are saved immediately.
        </Typography>
      </Box>
      <Divider />
      <TableContainer sx={{ maxHeight: "62vh", overflowX: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {/* Sticky cap-name column header */}
              <TableCell
                sx={{
                  ...stickyCell,
                  zIndex: 3,
                  minWidth: 230,
                  bgcolor: "background.paper",
                  fontWeight: 700,
                }}
              >
                Capability
              </TableCell>

              {roleEntries.map(([slug, role]) => (
                <TableCell
                  key={slug}
                  align="center"
                  sx={{ minWidth: 140, fontWeight: 700, verticalAlign: "top", pb: 0.5 }}
                >
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {role.name}
                  </Typography>
                  <Stack direction="row" justifyContent="center" spacing={0.5} sx={{ mt: 0.5 }}>
                    {role.is_protected && (
                      <Tooltip title="Reset to default caps">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => onReset(slug)}
                            disabled={saving}
                          >
                            <RestoreIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {!role.is_protected && (
                      <Tooltip title="Delete this role">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDelete(slug)}
                            disabled={saving}
                          >
                            <DeleteIcon fontSize="inherit" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {sectionEntries.map(([section, caps]) => (
              <React.Fragment key={section}>
                {/* Section divider row */}
                <TableRow>
                  <TableCell
                    colSpan={roleEntries.length + 1}
                    sx={{ bgcolor: "action.hover", py: 0.5, borderBottom: "none" }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
                    >
                      {section}
                    </Typography>
                  </TableCell>
                </TableRow>

                {caps.map((cap) => (
                  <TableRow key={cap} hover>
                    <TableCell
                      sx={{
                        ...stickyCell,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Tooltip
                        title={config.notes[cap] ?? ""}
                        placement="bottom"
                        disableHoverListener={!config.notes[cap]}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem", lineHeight: 1.2 }}>
                            {config.managed_caps[cap] ?? cap}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace", fontSize: "0.68rem" }}>
                            {cap}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>

                    {roleEntries.map(([slug, role]) => (
                      <TableCell key={slug} align="center" padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={!!(role.caps?.[cap])}
                          onChange={(e) => onToggle(slug, cap, e.target.checked)}
                          disabled={saving}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

// ── Add Role ─────────────────────────────────────────────────────────────────

const AddRoleForm = ({
  saving,
  onCreate,
}: {
  saving: boolean;
  onCreate: (slug: string, name: string) => Promise<boolean>;
}) => {
  const [name, setName] = useState("");

  const toSlug = (val: string) =>
    val
      .toLowerCase()
      .replace(/\s+/g, "_")
      .replace(/[^a-z0-9_]/g, "");

  const handleSubmit = async () => {
    const slug = toSlug(name);
    if (!slug || !name) return;
    const ok = await onCreate(slug, name);
    if (ok) setName("");
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Add New Role
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
        <TextField
          label="Display Name"
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          helperText={name ? `Slug: ${toSlug(name)}` : " "}
          sx={{ minWidth: 260 }}
        />
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || !toSlug(name)}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
          sx={{ mt: 0.25 }}
        >
          Create Role
        </Button>
      </Stack>
    </Paper>
  );
};

// ── User Overrides ────────────────────────────────────────────────────────────

const UserOverridesSection = ({
  overrides,
  managedCaps,
  saving,
  onAdd,
  onDelete,
}: {
  overrides: CapOverride[];
  managedCaps: { value: string; label: string }[];
  saving: boolean;
  onAdd: (userId: number, cap: string, granted: boolean) => Promise<boolean>;
  onDelete: (id: number) => Promise<boolean>;
}) => {
  const [userId, setUserId] = useState<number | null>(null);
  const [cap, setCap] = useState("");
  const [granted, setGranted] = useState(true);

  const handleAdd = async () => {
    if (!userId || !cap) return;
    const ok = await onAdd(userId, cap, granted);
    if (ok) {
      setUserId(null);
      setCap("");
      setGranted(true);
    }
  };

  const capOptions = [
    { value: "", label: "— Select capability —" },
    ...managedCaps,
  ];

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        User Capability Overrides
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Grant or deny a specific capability for an individual user, regardless of their role.
      </Typography>

      {overrides.length > 0 ? (
        <TableContainer sx={{ mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Capability</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {overrides.map((o) => (
                <TableRow key={o.id} hover>
                  <TableCell>
                      <UserIdentifierDisplay
                        firstName={o.user_first_name || o.user_display}
                        lastName={o.user_last_name || null}
                        role={o.user_role}
                        grade={o.user_grade ?? null}
                      />
                    </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
                      {o.cap}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={o.granted ? "success.main" : "error.main"}
                    >
                      {o.granted ? "Granted" : "Denied"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onDelete(o.id)}
                      disabled={saving}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No overrides configured.
        </Typography>
      )}

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle2" gutterBottom>
        Add Override
      </Typography>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start" flexWrap="wrap">
        <Box sx={{ minWidth: 280 }}>
          <UserAutocomplete
            label="User"
            value={userId}
            onChange={(id) => setUserId(id)}
          />
        </Box>
        <Box sx={{ minWidth: 260 }}>
          <OutlinedSelect
            label="Capability"
            value={cap}
            onChange={setCap}
            options={capOptions}
            size="medium"
          />
        </Box>
        <RadioGroup
          row
          value={granted ? "grant" : "deny"}
          onChange={(e) => setGranted(e.target.value === "grant")}
        >
          <FormControlLabel value="grant" control={<Radio size="small" />} label="Grant" />
          <FormControlLabel value="deny" control={<Radio size="small" />} label="Deny" />
        </RadioGroup>
        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={saving || !userId || !cap}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          Add Override
        </Button>
      </Stack>
    </Paper>
  );
};

// ── Main View ─────────────────────────────────────────────────────────────────

export const CapabilitiesView = () => {
  const {
    config,
    overrides,
    loading,
    saving,
    loadConfig,
    loadOverrides,
    toggleCap,
    createRole,
    deleteRole,
    resetRole,
    addOverride,
    deleteOverride,
  } = useCapabilitiesAdmin();

  useEffect(() => {
    loadConfig();
    loadOverrides();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ViewContainer sx={{ minHeight: "100vh", p: { xs: 1.5, md: 3 } }}>
      <Typography variant="h4" mb={3}>
        Capabilities &amp; Roles
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && config && (
        <Stack spacing={3} sx={{ maxWidth: contentMaxWidth.wide }}>
          <RoleMatrix
            config={config}
            saving={saving}
            onToggle={toggleCap}
            onReset={resetRole}
            onDelete={deleteRole}
          />

          <AddRoleForm saving={saving} onCreate={createRole} />

          <UserOverridesSection
            overrides={overrides}
            managedCaps={Object.entries(config.managed_caps ?? {}).map(([value, label]) => ({ value, label }))}
            saving={saving}
            onAdd={addOverride}
            onDelete={deleteOverride}
          />
        </Stack>
      )}
    </ViewContainer>
  );
};
